import React from 'react';
import './tableUserLinks.css';
import { Table, Typography, Button, Grid, Tooltip, Card, Tag, Empty, Pagination } from 'antd';
import { SendOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const TableUserLinks = ({ data, evaluationData, serviceData, pagination, onPageChange }) => {
    const screens = useBreakpoint();
    const useCardsLayout = !screens.lg;
    const isMobile = !screens.md;
    const isTabletCards = screens.md && !screens.lg;

    // Función para formatear texto con saltos de línea
    const formatTextWithLineBreaks = (text) => {
        if (!text) return '';

        return text.split('\n').map((line, index, array) => (
            <span key={index}>
                {line}
                {index < array.length - 1 && <br />}
            </span>
        ));
    };

    const goToEvaluation = (uuid) => {
        const rootUrl = window.location.origin;
        const link = `${rootUrl}/evaluaciones/form/${uuid}`;
        window.location.href = link;
    };

    const getStatusLabel = (record) => {
        if (record.evr_finished) {
            return 'Proceso finalizado';
        }
        if (record.evr_started) {
            return 'Proceso iniciado';
        }
        return 'Proceso sin iniciar';
    };

    const getStatusColor = (record) => {
        if (record.evr_finished) {
            return 'green';
        }
        if (record.evr_started) {
            return 'gold';
        }
        return 'default';
    };

    const formatDateValue = (value, isEnabled) => {
        if (!isEnabled || !value) {
            return 'PENDIENTE';
        }

        return new Intl.DateTimeFormat('es-CO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(new Date(value));
    };

    const columns = [
        {
            title: 'Identificación',
            dataIndex: 'evr_evaluated',
            key: 'evr_evaluated',
            width: 140,
        },
        {
            title: 'Nombre Completo',
            dataIndex: 'evr_evaluated_fullname',
            key: 'evr_evaluated_fullname',
            ellipsis: true,
            width: 240,
        },
        {
            title: 'Cargo',
            dataIndex: 'evr_evaluated_job_title',
            key: 'evr_evaluated_job_title',
            ellipsis: true,
            width: 220,
            responsive: ['md'],
        },
        {
            title: 'Relación',
            dataIndex: 'evr_type',
            key: 'evr_type',
            width: 120,
            responsive: ['sm'],
            render: (text) => text ? (
                <Tag className='table-user-links-table-relationship'>
                    {text}
                </Tag>
            ) : '-',
        },
        {
            title: 'Status',
            dataIndex: 'evr_finished',
            key: 'evr_finished',
            width: 120,
            render: (text, record) => (
                <Tag color={getStatusColor(record)} className='table-user-links-table-status'>
                    {getStatusLabel(record)}
                </Tag>
            ),
        },
        {
            title: 'Inicio',
            dataIndex: 'evr_started_at',
            key: 'evr_started_at',
            width: 150,
            responsive: ['lg'],
            render: (text, record) => formatDateValue(text, record.evr_started),
        },
        {
            title: 'Fin',
            dataIndex: 'evr_finished_at',
            key: 'evr_finished_at',
            width: 150,
            responsive: ['lg'],
            render: (text, record) => formatDateValue(text, record.evr_finished),
        },
        {
            title: '',
            dataIndex: 'btn_start',
            key: 'btn_start',
            fixed: 'right',
            width: 132,
            align: 'center',
            render: (text, record) => {
                if (record.evr_finished) {
                    return <span className='table-user-links-action-disabled'>-</span>;
                }

                return (
                    <div className='table-user-links-action'>
                        <Tooltip title='Iniciar evaluación'>
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                size='large'
                                className='table-user-links-action-button'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    goToEvaluation(record.evr_uuid);
                                }}
                            >
                                INICIAR
                            </Button>
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    const renderMobileCards = () => {
        if (!data || data.length === 0) {
            return (
                <div className='table-user-links-mobile-list'>
                    <Empty description='No hay evaluaciones pendientes para este documento.' />
                </div>
            );
        }

        return (
            <div className='table-user-links-mobile-list'>
                {data.map((record) => {
                    const isFinished = record.evr_finished;
                    const statusLabel = getStatusLabel(record);

                    return (
                        <Card
                            key={record.evr_uuid}
                            className={`table-user-links-card${isFinished ? ' is-finished' : ''}${isTabletCards ? ' is-tablet' : ''}`}
                            hoverable={!isFinished}
                            onClick={isFinished ? undefined : () => goToEvaluation(record.evr_uuid)}
                        >
                            <div className='table-user-links-card-header'>
                                <div className='table-user-links-card-title-wrap'>
                                    <Text strong className='table-user-links-card-title'>
                                        {record.evr_evaluated_fullname || 'Sin nombre'}
                                    </Text>
                                    <Text className='table-user-links-card-id'>
                                        CC {record.evr_evaluated}
                                    </Text>
                                </div>
                                <div className='table-user-links-card-tags'>
                                    <Tag color={getStatusColor(record)} className='table-user-links-card-status'>
                                        {statusLabel}
                                    </Tag>
                                    {record.evr_type ? (
                                        <Tag className='table-user-links-card-relationship'>
                                            {record.evr_type}
                                        </Tag>
                                    ) : null}
                                </div>
                            </div>

                            <div className='table-user-links-card-body'>
                                <div className='table-user-links-card-inline-row'>
                                    <span className='table-user-links-card-inline-label'>Cargo:</span>
                                    <span className='table-user-links-card-inline-value'>
                                        {record.evr_evaluated_job_title || 'Sin cargo'}
                                    </span>
                                </div>
                                <div className='table-user-links-card-dates'>
                                    <div className='table-user-links-card-date-item'>
                                        <span className='table-user-links-card-date-label'>Inicio:</span>
                                        <span className='table-user-links-card-date-value'>
                                            {formatDateValue(record.evr_started_at, record.evr_started)}
                                        </span>
                                    </div>
                                    <div className='table-user-links-card-date-item'>
                                        <span className='table-user-links-card-date-label'>Fin:</span>
                                        <span className='table-user-links-card-date-value'>
                                            {formatDateValue(record.evr_finished_at, record.evr_finished)}
                                        </span>
                                    </div>
                                </div>
                                {!isMobile && (
                                    <div className='table-user-links-card-inline-row is-muted'>
                                        <span className='table-user-links-card-inline-label'>Estado:</span>
                                        <span className='table-user-links-card-inline-value'>
                                            {statusLabel}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className='table-user-links-card-footer'>
                                <Button
                                    type='primary'
                                    icon={<SendOutlined />}
                                    size={isMobile ? 'large' : 'middle'}
                                    block={isMobile}
                                    disabled={isFinished}
                                    className='table-user-links-card-button'
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (!isFinished) {
                                            goToEvaluation(record.evr_uuid);
                                        }
                                    }}
                                >
                                    {isFinished ? 'Proceso finalizado' : isMobile ? 'INICIAR EVALUACION' : 'INICIAR EVALUACION'}
                                </Button>
                            </div>
                        </Card>
                    );
                })}
                {pagination?.total > 0 && (
                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Pagination
                            current={pagination.page}
                            pageSize={pagination.pageSize}
                            total={pagination.total}
                            showSizeChanger={false}
                            onChange={(page, pageSize) => onPageChange?.(page, pageSize)}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (<div className='table-user-links-container'>
        {/* {
            evaluationData.evl_srv_id !== 3 && <h2 style={{ textAlign: 'center' }}>Personas a Evaluar</h2>
        } */}
        {
            serviceData && <h2 className='table-user-links-title'>{serviceData.srv_page_title}</h2>
        }
        <Paragraph className='table-user-links-intro'>
            {
                evaluationData && evaluationData.evl_main_welcome_text
                    ? formatTextWithLineBreaks(evaluationData.evl_main_welcome_text)
                    : serviceData && serviceData.srv_page_description
                        ? formatTextWithLineBreaks(serviceData.srv_page_description)
                        : `A continuación, encontrarás la lista de las personas que deberás evaluar en relación con su ejercicio de liderazgo, junto con la descripción de la relación que tienes con cada una de ellas (por ejemplo: autoevaluación, jefe, colaborador, par, cliente interno). Da click en cada fila para acceder.`
            }
        </Paragraph>
        {useCardsLayout ? renderMobileCards() : (
            <div className='table-user-links-table-wrap'>
                <Table
                    className='table-user-links'
                    columns={columns}
                    dataSource={data}
                    rowKey="evr_uuid"
                    size='middle'
                    pagination={{
                        current: pagination?.page || 1,
                        pageSize: pagination?.pageSize || 20,
                        total: pagination?.total || data?.length || 0,
                        size: 'small',
                        showSizeChanger: false,
                    }}
                    onChange={(pager) => onPageChange?.(pager.current, pager.pageSize)}
                    tableLayout='auto'
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'No hay evaluaciones pendientes para este documento.' }}
                    onRow={(record) => {
                        if (record.evr_finished) {
                            return {};
                        }
                        return {
                            onClick: () => goToEvaluation(record.evr_uuid),
                            style: { cursor: 'pointer' }
                        };
                    }}
                />
            </div>
        )}
    </div>
    );
};

export default TableUserLinks;
